import * as React from 'react';
import { Modal, ModalBody, ModalHeader, ListGroup, ListGroupItem } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { AppointmentData } from '../../models';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { formatEuros } from '../../shared/string';
import { serviceName } from '../../shared/appointmint/appointment';
import { useGeneralSettings } from '../../store/settings/hooks';
import { openAppointment } from '../../store/entities/actions';
import AppButton from '../../components/AppButton';

export interface ProfessionalSalaryModalProps {
    isOpen: boolean;
    onClose: () => void;
    professionalName: string;
    professionalParcel: number;
    totalProduced: number;
    summary: string[];
    appointments: AppointmentData[];
    onCreateExpense: () => void;
}

const ProfessionalSalaryModal: React.FC<ProfessionalSalaryModalProps> = ({
    isOpen,
    onClose,
    professionalName,
    professionalParcel,
    totalProduced,
    summary,
    appointments,
    onCreateExpense,
}) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useGeneralSettings();

    const operatingMargin = totalProduced - professionalParcel;
    const operatingMarginPercent = totalProduced > 0
        ? ((operatingMargin / totalProduced) * 100).toFixed(1) + '%'
        : '0%';

    const openAppointmentCallback = React.useCallback(
        (a: AppointmentData) => () => dispatch(openAppointment(a.id)),
        [dispatch],
    );

    return (
        <Modal size='lg' isOpen={isOpen} toggle={onClose}>
            <ModalHeader toggle={onClose}>{professionalName}</ModalHeader>
            <ModalBody>
                <div>
                    <div style={{display: 'flex'}}>
                        <div style={{flexGrow: 1}}>
                            <h5>
                                <b>{t(TK.professionalParcel)}: </b>
                                {formatEuros(professionalParcel)}
                            </h5>
                            <i style={{display: 'block'}}>
                                <b>{t(TK.effectiveValueProducedByProfessional)}: </b>
                                {formatEuros(totalProduced)}
                            </i>
                            <i style={{display: 'block'}}>
                                <b>{t(TK.operatingMargin)}: </b>
                                {formatEuros(operatingMargin)} ({operatingMarginPercent})
                            </i>
                        </div>
                        <div>
                            <AppButton color="success" onClick={onCreateExpense}>
                                {t(TK.registerPayment)}
                            </AppButton>
                        </div>
                    </div>

                    <hr/>

                    <h5><b>{t(TK.calculation)}</b></h5>
                    <ul>
                        {summary.map((parcel, i) => <li key={i}>{parcel.trim()}</li>)}
                    </ul>

                    <hr/>

                    <h5>{t(TK.appointments)}</h5>
                    <ListGroup>
                        {appointments.map(a => (
                            <ListGroupItem key={a.id}>
                                <AppButton color='link' style={{padding: 0, verticalAlign: 'top'}} onClick={openAppointmentCallback(a)}>
                                    {a.date} {a.start}
                                </AppButton>
                                &nbsp;- <b>{a.clientName}</b> ({serviceName(a.type, generalSettings)}) - {formatEuros(a.actualPriceWithPack)}
                            </ListGroupItem>
                        ))}
                    </ListGroup>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default ProfessionalSalaryModal;
